import type {
  ComponentAppearance,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';

/**
 * Token set for a given variant, exposing literal Tailwind classes.
 * Keep values as string literals to satisfy Tailwind's class scanner.
 */
export type VariantTokens = {
  bg50: string;
  bg100: string;
  bg500: string;
  text600: string;
  text700: string;
  ring50: string;
  ring100: string;
  ring500: string;
  ring700: string;
  border50: string;
  border100: string;
  border200: string;
  border300: string;
  border500: string;
  border700: string;
  solid: string; // solid surface (e.g., for prominent buttons)
};

/**
 * Literal mapping from variant → Tailwind tokens.
 * Note: For historical consistency, the 'info' variant uses the blue palette.
 */
export const variantTokens: Record<ComponentVariant, VariantTokens> = {
  primary: {
    bg50: 'bg-primary-50',
    bg100: 'bg-primary-100',
    bg500: 'bg-primary-500',
    text600: 'text-primary-600',
    text700: 'text-primary-700',
    ring50: 'ring-primary-50',
    ring100: 'ring-primary-100',
    ring500: 'ring-primary-500',
    ring700: 'ring-primary-700',
    border50: 'border-primary-50',
    border100: 'border-primary-100',
    border200: 'border-primary-200',
    border300: 'border-primary-300',
    border500: 'border-primary-500',
    border700: 'border-primary-700',
    solid: 'bg-primary-500 text-white',
  },
  secondary: {
    bg50: 'bg-secondary-50',
    bg100: 'bg-secondary-100',
    bg500: 'bg-secondary-500',
    text600: 'text-secondary-600',
    text700: 'text-secondary-700',
    ring50: 'ring-secondary-50',
    ring100: 'ring-secondary-100',
    ring500: 'ring-secondary-500',
    ring700: 'ring-secondary-700',
    border50: 'border-secondary-50',
    border100: 'border-secondary-100',
    border200: 'border-secondary-200',
    border300: 'border-secondary-300',
    border500: 'border-secondary-500',
    border700: 'border-secondary-700',
    solid: 'bg-secondary-500 text-white',
  },
  neutral: {
    bg50: 'bg-neutral-50',
    bg100: 'bg-neutral-100',
    bg500: 'bg-neutral-500',
    text600: 'text-neutral-600',
    text700: 'text-neutral-700',
    ring50: 'ring-neutral-50',
    ring100: 'ring-neutral-100',
    ring500: 'ring-neutral-500',
    ring700: 'ring-neutral-700',
    border50: 'border-neutral-50',
    border100: 'border-neutral-100',
    border200: 'border-neutral-200',
    border300: 'border-neutral-300',
    border500: 'border-neutral-500',
    border700: 'border-neutral-700',
    solid: 'bg-neutral-500 text-white',
  },
  danger: {
    bg50: 'bg-danger-50',
    bg100: 'bg-danger-100',
    bg500: 'bg-danger-500',
    text600: 'text-danger-600',
    text700: 'text-danger-700',
    ring50: 'ring-danger-50',
    ring100: 'ring-danger-100',
    ring500: 'ring-danger-500',
    ring700: 'ring-danger-700',
    border50: 'border-danger-50',
    border100: 'border-danger-100',
    border200: 'border-danger-200',
    border300: 'border-danger-300',
    border500: 'border-danger-500',
    border700: 'border-danger-700',
    solid: 'bg-danger-500 text-white',
  },
  warning: {
    bg50: 'bg-warning-50',
    bg100: 'bg-warning-100',
    bg500: 'bg-warning-500',
    text600: 'text-warning-600',
    text700: 'text-warning-700',
    ring50: 'ring-warning-50',
    ring100: 'ring-warning-100',
    ring500: 'ring-warning-500',
    ring700: 'ring-warning-700',
    border50: 'border-warning-50',
    border100: 'border-warning-100',
    border200: 'border-warning-200',
    border300: 'border-warning-300',
    border500: 'border-warning-500',
    border700: 'border-warning-700',
    solid: 'bg-warning-500 text-white',
  },
  success: {
    bg50: 'bg-success-50',
    bg100: 'bg-success-100',
    bg500: 'bg-success-500',
    text600: 'text-success-600',
    text700: 'text-success-700',
    ring50: 'ring-success-50',
    ring100: 'ring-success-100',
    ring500: 'ring-success-500',
    ring700: 'ring-success-700',
    border50: 'border-success-50',
    border100: 'border-success-100',
    border200: 'border-success-200',
    border300: 'border-success-300',
    border500: 'border-success-500',
    border700: 'border-success-700',
    solid: 'bg-success-500 text-white',
  },
  info: {
    bg50: 'bg-blue-50',
    bg100: 'bg-blue-100',
    bg500: 'bg-blue-500',
    text600: 'text-blue-600',
    text700: 'text-blue-700',
    ring50: 'ring-blue-50',
    ring100: 'ring-blue-100',
    ring500: 'ring-blue-500',
    ring700: 'ring-blue-700',
    border50: 'border-blue-50',
    border100: 'border-blue-100',
    border200: 'border-blue-200',
    border300: 'border-blue-300',
    border500: 'border-blue-500',
    border700: 'border-blue-700',
    solid: 'bg-blue-500 text-white',
  },
  whatsapp: {
    bg50: 'bg-whatsapp-50',
    bg100: 'bg-whatsapp-100',
    bg500: 'bg-whatsapp-500',
    text600: 'text-whatsapp-600',
    text700: 'text-whatsapp-700',
    ring50: 'ring-whatsapp-50',
    ring100: 'ring-whatsapp-100',
    ring500: 'ring-whatsapp-500',
    ring700: 'ring-whatsapp-700',
    border50: 'border-whatsapp-50',
    border100: 'border-whatsapp-100',
    border200: 'border-whatsapp-200',
    border300: 'border-whatsapp-300',
    border500: 'border-whatsapp-500',
    border700: 'border-whatsapp-700',
    solid: 'bg-whatsapp-500 text-white',
  },
  call: {
    bg50: 'bg-call-50',
    bg100: 'bg-call-100',
    bg500: 'bg-call-500',
    text600: 'text-call-600',
    text700: 'text-call-700',
    ring50: 'ring-call-50',
    ring100: 'ring-call-100',
    ring500: 'ring-call-500',
    ring700: 'ring-call-700',
    border50: 'border-call-50',
    border100: 'border-call-100',
    border200: 'border-call-200',
    border300: 'border-call-300',
    border500: 'border-call-500',
    border700: 'border-call-700',
    solid: 'bg-call-500 text-white',
  },
  email: {
    bg50: 'bg-email-50',
    bg100: 'bg-email-100',
    bg500: 'bg-email-500',
    text600: 'text-email-600',
    text700: 'text-email-700',
    ring50: 'ring-email-50',
    ring100: 'ring-email-100',
    ring500: 'ring-email-500',
    ring700: 'ring-email-700',
    border50: 'border-email-50',
    border100: 'border-email-100',
    border200: 'border-email-200',
    border300: 'border-email-300',
    border500: 'border-email-500',
    border700: 'border-email-700',
    solid: 'bg-email-500 text-white',
  },
};

/**
 * Resolves color classes for a component surface given a variant + appearance.
 * Returns class names covering bg, text, ring, and border color (no hover/active).
 * Components can opt-in to ring by adding ring width utilities, or to borders by
 * adding a border width utility; the color classes are provided by this resolver.
 */
export const resolveVariantClasses = (
  variant: ComponentVariant,
  appearance: ComponentAppearance
): string => {
  const tokens = variantTokens[variant];
  switch (appearance) {
    case 'soft': {
      // Tinted surface with subtle border
      return [tokens.bg50, 'border', tokens.border100, tokens.text600].join(
        ' '
      );
    }
    case 'filled': {
      // Emphasized surface with stronger tint and light border
      return [tokens.bg100, 'border', tokens.border100, tokens.text700].join(
        ' '
      );
    }
    case 'outline': {
      // White surface with colored outline
      return ['bg-white', 'border', tokens.border300, tokens.text600].join(' ');
    }
    case 'ghost':
    default: {
      // Transparent background; rely on text color only
      return ['bg-transparent', tokens.text600].join(' ');
    }
  }
};
